import React, { useEffect } from "react";
import { Box } from "@mui/material";
import ViewerPlaybookPlay from "./ViewerPlaybookPlay";
import QuillDisplay from "../../components/QuillDisplay";
import {
  useWindowVirtualizer,
  observeWindowOffset,
} from "@tanstack/react-virtual";
import { useRef } from "react";
import useIsFirstRender from "../../hooks/useIsFirstRender.js";
import { useSsr } from "usehooks-ts";

function easeOutQuint(t) {
  return 1 + --t * t * t * t * t;
}

export default function ViewerPlaybookChapter({
  content,

  setActiveItem,
  scrollToObject,
  setScrollToObject,
}) {
  const listRef = useRef(null);
  const skipScrollRef = React.useRef(false);

  const virtualizer = useWindowVirtualizer({
    initialRect: {
      height: 1000,
      width: 0,
      x: 0,
      y: 0,
    },
    count: content.length,
    estimateSize: () => 1000,
    overscan: 4,
    scrollMargin: listRef.current?.offsetTop ?? 0,
    observeElementOffset: (instance, cb) =>
      observeWindowOffset(instance, (offset) => {
        if (skipScrollRef.current) {
          skipScrollRef.current = false;
          return;
        }
        cb(offset);
      }),
  });

  const { isBrowser } = useSsr();

  const useSmoothScroll = (virtualizer) => {
    const scrollingRef = React.useRef(0);

    return React.useCallback(
      (
        index,
        { align: initialalign, duration = 1000, scrollPadding = 80 } = {}
      ) => {
        const direction =
          virtualizer.getOffsetForIndex(index, initialalign)[0] -
          virtualizer.scrollOffset;

        const start =
          virtualizer.getOffsetForIndex(index, initialalign)[0] +
          (direction > 0 ? -200 : 200) -
          scrollPadding;

        const startTime = (scrollingRef.current = Date.now());
        const [, align] = virtualizer.getOffsetForIndex(index, initialalign);

        const run = () => {
          if (scrollingRef.current !== startTime) return;
          const now = Date.now();
          const elapsed = now - startTime;
          const progress = easeOutQuint(Math.min(elapsed / duration, 1));
          const offset =
            virtualizer.getOffsetForIndex(index, align)[0] - scrollPadding;
          const interpolated = start + (offset - start) * progress;

          if (elapsed < duration) {
            virtualizer.scrollToOffset(interpolated, { align: "start" });
            requestAnimationFrame(run);
          } else {
            virtualizer.scrollToOffset(interpolated, { align: "start" });
          }
        };

        requestAnimationFrame(run);
      },
      [virtualizer]
    );
  };

  const smoothScrollToIndex = useSmoothScroll(virtualizer);

  const previousRange = useRef(virtualizer.range);

  useEffect(() => {
    const currentStartIndex = virtualizer?.range?.startIndex;
    const currentEndIndex = virtualizer?.range?.endIndex;
    const previousStartIndex = previousRange?.current?.startIndex;
    const previousEndIndex = previousRange?.current?.endIndex;

    if (currentStartIndex < previousStartIndex) {
      let contentObject = content[currentStartIndex];

      setActiveItem({
        chapter: contentObject.chapterIndex,
        object: contentObject.objectIndex ?? 0,
      });
    } else if (currentEndIndex > previousEndIndex) {
      let contentObject = content[currentEndIndex];
      setActiveItem({
        chapter: contentObject.chapterIndex,
        object: contentObject.objectIndex ?? 0,
      });
    }

    previousRange.current = virtualizer.range;
  }, [virtualizer?.range?.startIndex, virtualizer?.range?.endIndex]);

  useEffect(() => {
    if (scrollToObject.object !== null && scrollToObject.chapter !== null) {
      let scrollIndex = content.findIndex(
        (item) =>
          item.chapterIndex === scrollToObject.chapter &&
          item.objectIndex === scrollToObject.object
      );

      smoothScrollToIndex(scrollIndex, { align: "start" });
      setTimeout(() => {
        setActiveItem(scrollToObject);
      }, 300);

      setScrollToObject({
        chapter: null,
        object: null,
      });
    }
  }, [scrollToObject]);

  const isFirstRender = useIsFirstRender();

  return (
    <Box>
      <Box sx={{ mt: 2 }} ref={listRef}>
        <Box
          sx={{
            height: isBrowser ? `${virtualizer.getTotalSize()}px` : undefined,
            width: "100%",
            position: "relative",
          }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const item = content[virtualRow.index];
            return (
              <Box
                key={item.chapterIndex + "-" + virtualRow.key}
                ref={virtualizer.measureElement}
                style={{
                  position: isBrowser ? "absolute" : undefined,
                  width: "100%",
                  transform: isBrowser
                    ? `translateY(${
                        virtualRow.start - virtualizer.options.scrollMargin
                      }px)`
                    : undefined,
                }}
                data-index={virtualRow.index}
              >
                {item.type === "chapterHeader" && (
                  <Box
                    data-chapter-header={item.chapterIndex}
                    component="h2"
                    sx={{
                      mt: item.chapterIndex === 0 ? 4 : 10,
                      mb: 5,
                      fontWeight: 600,
                      fontSize: {
                        xs: 24,
                        sm: 30,
                        md: 37,
                      },
                    }}
                  >
                    {item.chapterIndex + 1}.{" " + item.title}
                  </Box>
                )}

                {item.type === "play" && (
                  <>
                    <ViewerPlaybookPlay
                      skipScrollRef={skipScrollRef}
                      chapterIndex={item.chapterIndex}
                      objectIndex={virtualRow.index}
                      play={item.data}
                      uuid={item.chapterIndex + "-" + virtualRow.index}
                    />

                    <hr
                      style={{
                        marginTop: 40,
                        marginBottom: 45,
                        borderTop: "1px solid #ddd",
                      }}
                    />
                  </>
                )}
                {item.type === "text" && (
                  <>
                    <Box
                      sx={{
                        mb: 6,
                      }}
                    >
                      <Box
                        data-chapter={item.chapterIndex}
                        data-object={virtualRow.index}
                        component="h3"
                        sx={{
                          mb: 2,
                          fontWeight: 600,
                          fontSize: {
                            xs: 20,
                            sm: 23,
                            md: 25,
                          },
                        }}
                      >
                        {item.title}
                      </Box>

                      <QuillDisplay content={item.data} />
                    </Box>
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
